<template>
  <div class="accommodation-requests">
    <b-card>
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">
          Demandes d'hébergement
        </h4>
        <div class="d-flex align-items-center">
          <b-button
            v-if="!isAccommodationReadOnly"
            v-b-tooltip.hover
            :title="showExportBar ? 'Fermer l\'export' : 'Exporter en Excel'"
            :variant="showExportBar ? 'primary' : 'outline-primary'"
            size="sm"
            class="mr-1 d-flex align-items-center"
            @click="showExportBar = !showExportBar"
          >
            <feather-icon :icon="showExportBar ? 'XIcon' : 'DownloadIcon'" size="14" class="mr-50" />
            Exporter
          </b-button>
          <b-form-checkbox v-model="showArchived" switch @change="onIncludeArchivedChange">
            Inclure les archives
          </b-form-checkbox>
        </div>
      </b-card-header>

      <!-- Barre d'export inline -->
      <b-collapse v-if="!isAccommodationReadOnly" v-model="showExportBar">
        <div class="export-bar px-3 py-2 border-top border-bottom">
          <div class="export-bar-content d-flex justify-content-between align-items-center flex-wrap">
            <!-- Partie gauche : Label + Input + Messages -->
            <div class="export-bar-controls d-flex align-items-center flex-grow-1 flex-wrap">
              <div class="d-flex align-items-center text-primary">
                <feather-icon icon="FileTextIcon" size="18" class="mr-50" />
                <span class="font-weight-bold">Créer un export Excel</span>
              </div>

              <b-input-group size="sm" class="export-range-input input-group-merge flex-grow-1 shadow-sm">
                <b-input-group-prepend is-text>
                  <feather-icon icon="CalendarIcon" size="14" />
                </b-input-group-prepend>
                <flat-pickr
                  ref="exportPicker"
                  :value="exportRangeValue"
                  class="form-control"
                  :config="exportRangeConfig"
                  placeholder="Sélectionner (début - fin)..."
                />
                <b-input-group-append v-if="exportStart || exportEnd" is-text class="cursor-pointer" @click="clearExportRange">
                  <feather-icon icon="XIcon" size="14" class="text-muted hover-danger" />
                </b-input-group-append>
              </b-input-group>

              <!-- Messages d'aide animés -->
              <div class="export-hint d-flex align-items-center">
                <transition name="fade" mode="out-in">
                  <span v-if="exportStart && !exportEnd" key="helper" class="text-primary font-weight-bold small animate-pulse">
                    <feather-icon icon="ArrowRightIcon" size="12" class="mr-50" />Sélectionnez la date de fin
                  </span>
                  <span v-else-if="exportError" key="error" class="text-danger font-weight-bold small">
                    <feather-icon icon="AlertCircleIcon" size="12" class="mr-50" />{{ exportError }}
                  </span>
                  <span v-else-if="!exportStart && !exportEnd" key="info" class="text-muted font-weight-bold small">
                    <feather-icon icon="InfoIcon" size="12" class="mr-50" />Laisser vide pour tout exporter
                  </span>
                </transition>
              </div>
            </div>

            <!-- Partie droite : Bouton d'action -->
            <b-button
              variant="primary"
              size="sm"
              class="d-flex align-items-center shadow-sm font-weight-bold"
              :disabled="!isExportRangeValid || isExporting"
              @click="handleExport"
            >
              <b-spinner v-if="isExporting" small class="mr-50" />
              <feather-icon v-else icon="DownloadIcon" size="16" class="mr-50" />
              <span class="text-nowrap">{{ isExporting ? 'Génération...' : 'Télécharger le fichier' }}</span>
            </b-button>
          </div>
        </div>
      </b-collapse>
      <b-card-body>
        <b-alert v-if="error" variant="danger" show dismissible @dismissed="clearError">
          {{ error }}
        </b-alert>

        <!-- Barre d'outils et Recherche -->
        <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap">
          <div class="d-flex align-items-center flex-grow-1 mb-1 mb-md-0">
            <b-input-group size="sm" class="input-group-merge round search-input-group shadow-sm mr-1" style="max-width: 350px;">
              <b-input-group-prepend is-text>
                <feather-icon
                  icon="SearchIcon"
                  size="14"
                  :class="searchTerm ? 'text-primary' : 'text-muted'"
                />
              </b-input-group-prepend>
              <b-form-input
                v-model="searchTerm"
                placeholder="Rechercher par n° unique, nom, prénom ou téléphone..."
                debounce="400"
                class="search-input"
                @update="onSearchChange"
              />
              <b-input-group-append is-text>
                <b-spinner
                  v-if="loading && searchTerm"
                  small
                  variant="primary"
                />
                <feather-icon
                  v-else-if="searchTerm"
                  icon="XIcon"
                  size="14"
                  class="text-muted cursor-pointer hover-danger"
                  @click="clearSearch"
                />
                <feather-icon
                  v-else
                  v-b-tooltip.hover
                  icon="HelpCircleIcon"
                  size="14"
                  class="text-muted cursor-help"
                  title="Recherchez par n° unique (ex: 2024-001), nom, prénom ou numéro de téléphone"
                />
              </b-input-group-append>
            </b-input-group>

            <transition name="fade">
              <b-badge
                v-if="searchTerm && !loading"
                variant="light-primary"
                pill
                class="font-weight-bold"
              >
                {{ pagination.totalCount }} résultat{{ pagination.totalCount > 1 ? 's' : '' }}
              </b-badge>
            </transition>
          </div>

          <div v-if="searchTerm || showArchived" class="d-flex align-items-center">
            <b-button
              variant="flat-secondary"
              size="sm"
              class="text-nowrap"
              @click="resetAllFilters"
            >
              <feather-icon icon="RotateCcwIcon" size="14" class="mr-25" />
              Réinitialiser les filtres
            </b-button>
          </div>
        </div>

        <b-table
          :items="groupedRequests"
          :fields="fields"
          :busy="loading"
          responsive
          striped
          hover
          show-empty
          empty-text="Aucune demande d'hébergement"
          :tbody-tr-class="getRowClass"
          @row-clicked="onRowClicked"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle mr-2" />
              <strong>Chargement...</strong>
            </div>
          </template>

          <template #cell(receivedAt)="data">
            <span class="text-nowrap">{{ formatDate(data.value) }}</span>
          </template>

          <template #cell(youngDetails)="data">
            <div class="d-flex align-items-center">
              <b-button
                v-if="data.item._requestCount > 1"
                v-b-tooltip.hover
                :title="data.item._showDetails ? 'Masquer les demandes précédentes' : 'Voir les demandes précédentes'"
                variant="flat-secondary"
                size="sm"
                class="btn-icon rounded-circle history-toggle-btn mr-50"
                :aria-label="data.item._showDetails ? 'Masquer l\'historique des demandes' : 'Voir l\'historique des demandes'"
                :aria-expanded="data.item._showDetails ? 'true' : 'false'"
                @click.stop="toggleGroupDetails(data.item)"
              >
                <feather-icon :icon="data.item._showDetails ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="16" />
              </b-button>
              <b-media vertical-align="center" class="flex-grow-1">
                <template #aside>
                  <b-avatar
                    size="32"
                    :variant="data.item.isArchived ? 'light-secondary' : 'light-primary'"
                    :text="getInitials(data.item.firstName, data.item.lastName)"
                  />
                </template>
                <div class="young-details-header d-flex align-items-center flex-wrap">
                  <div class="font-weight-bold d-block text-nowrap" :class="data.item.isArchived ? 'text-body' : 'text-primary'">
                    {{ (data.item.lastName || '').toUpperCase() }} {{ data.item.firstName || '' }}
                  </div>
                  <b-badge
                    v-if="data.item.isArchived"
                    variant="light-primary"
                    class="archive-state-badge"
                  >
                    <feather-icon icon="ArchiveIcon" size="12" class="mr-25" />
                    Archivée
                  </b-badge>
                  <b-badge
                    v-if="data.item._isFollowUp"
                    v-b-tooltip.hover
                    title="Une précédente demande archivée existe pour ce jeune"
                    variant="light-warning"
                    class="follow-up-request-badge"
                  >
                    <feather-icon icon="RepeatIcon" size="12" class="mr-25" />
                    Relance
                  </b-badge>
                  <b-badge
                    v-if="data.item._requestCount > 1"
                    v-b-tooltip.hover
                    title="Nombre total de demandes pour ce jeune"
                    variant="light-info"
                    pill
                    class="history-count-badge"
                  >
                    {{ data.item._requestCount }} demandes
                  </b-badge>
                </div>
                <small class="text-muted">#{{ data.item.uniqueYoungNumber || '' }}</small>
              </b-media>
            </div>
          </template>

          <template #row-details="row">
            <div class="history-panel px-2 py-2">
              <h6 class="text-muted mb-1">
                Historique des demandes de {{ row.item.firstName }} {{ (row.item.lastName || '').toUpperCase() }}
              </h6>
              <b-table
                :items="row.item._history"
                :fields="historyFields"
                small
                borderless
                responsive
                class="mb-0 history-table"
              >
                <template #cell(receivedAt)="historyData">
                  <span class="text-nowrap">{{ formatDate(historyData.value) }}</span>
                </template>
                <template #cell(status)="historyData">
                  <b-badge :variant="historyData.item.isArchived ? 'light-secondary' : (historyData.item.appointmentDateTime ? 'light-success' : 'light-warning')">
                    {{ historyData.item.isArchived ? 'Archivée' : (historyData.item.appointmentDateTime ? 'RDV Fixé' : 'À planifier') }}
                  </b-badge>
                </template>
                <template #cell(followUpStatus)="historyData">
                  {{ getFollowUpStatusLabel(historyData.item.followUpStatus) }}
                </template>
                <template #cell(appointmentDateTime)="historyData">
                  <span class="text-nowrap">{{ formatDate(historyData.item.appointmentDateTime) }}</span>
                </template>
                <template #cell(historyActions)="historyData">
                  <b-button
                    v-if="!isAccommodationReadOnly"
                    v-b-tooltip.hover
                    :title="historyData.item.isArchived ? 'Réactiver la demande' : 'Archiver la demande'"
                    :variant="historyData.item.isArchived ? 'flat-success' : 'flat-danger'"
                    class="btn-icon rounded-circle"
                    @click.stop="confirmArchive(historyData.item)"
                  >
                    <feather-icon :icon="historyData.item.isArchived ? 'RefreshCwIcon' : 'ArchiveIcon'" size="14" />
                  </b-button>
                </template>
              </b-table>
            </div>
          </template>

          <template #cell(department)="data">
            <b-badge variant="light-secondary">
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="data.item.appointmentDateTime ? 'light-success' : 'light-warning'">
              {{ data.item.appointmentDateTime ? 'RDV Fixé' : 'À planifier' }}
            </b-badge>
          </template>

          <template #cell(followUpStatus)="data">
            <div class="follow-up-cell" @click.stop>
              <v-select
                v-if="!isAccommodationReadOnly"
                class="follow-up-select"
                :value="data.item.followUpStatus"
                :options="followUpStatusOptions"
                :reduce="option => option.id"
                label="label"
                placeholder="— Choisir —"
                :clearable="false"
                append-to-body
                :calculate-position="calculateFollowUpDropdownPosition"
                @input="handleFollowUpStatusChange(data.item, $event)"
              />
              <span v-else class="follow-up-value">{{ getFollowUpStatusLabel(data.item.followUpStatus) }}</span>
            </div>
          </template>

          <template #cell(appointmentDateTime)="data">
            <div class="appointment-cell" @click.stop>
              <b-input-group v-if="!isAccommodationReadOnly" size="sm" class="input-group-merge">
                <b-input-group-prepend is-text>
                  <feather-icon icon="CalendarIcon" />
                </b-input-group-prepend>
                <flat-pickr
                  :value="data.item.appointmentDateTime"
                  class="form-control"
                  :config="flatPickrConfig"
                  :aria-label="`Date de rendez-vous pour ${data.item.firstName} ${data.item.lastName}`"
                  placeholder="Cliquez pour définir"
                  @input="handleUpdateAppointment(data.item, $event)"
                />
              </b-input-group>
              <span v-else class="text-nowrap">{{ formatDate(data.item.appointmentDateTime) }}</span>
            </div>
          </template>

          <template #cell(actions)="data">
            <div class="actions-cell text-center text-nowrap" @click.stop>
              <b-button
                v-b-tooltip.hover
                title="Consulter la fiche"
                variant="flat-primary"
                class="btn-icon rounded-circle"
                :aria-label="`Consulter la fiche de ${data.item.firstName} ${data.item.lastName}`"
                @click.stop="openYoungDetails(data.item.youngId)"
              >
                <feather-icon
                  icon="EyeIcon"
                  size="16"
                />
              </b-button>
              <template v-if="!isAccommodationReadOnly">
                <b-button
                  v-if="!data.item.isArchived"
                  v-b-tooltip.hover
                  title="Archiver la demande"
                  variant="flat-danger"
                  class="btn-icon rounded-circle ml-1"
                  :aria-label="`Archiver la demande de ${data.item.firstName} ${data.item.lastName}`"
                  @click.stop="confirmArchive(data.item)"
                >
                  <feather-icon
                    icon="ArchiveIcon"
                    size="16"
                  />
                </b-button>
                <b-button
                  v-else
                  v-b-tooltip.hover
                  title="Réactiver la demande"
                  variant="flat-success"
                  class="btn-icon rounded-circle ml-1"
                  :aria-label="`Réactiver la demande de ${data.item.firstName} ${data.item.lastName}`"
                  @click.stop="confirmArchive(data.item)"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="16"
                  />
                </b-button>
              </template>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <span v-if="pagination.totalCount > 0" class="pagination-panel mt-3">
          <b-form-select
            v-model="perPage"
            class="pagination-panel__select"
            :options="perPageOptions"
          />
          <b-pagination
            v-model="currentPage"
            :total-rows="pagination.totalCount"
            :per-page="perPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="mb-0"
            @change="onPageChange"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
          <p class="displaynbResult">{{ paginationInfo }}</p>
        </span>
      </b-card-body>
    </b-card>

    <!-- Modal de confirmation d'archivage/désarchivage -->
    <b-modal
      ref="archiveModal"
      centered
      hide-header-close
      :title="archiveModalTitle"
      :ok-title="archiveModalOkTitle"
      :ok-variant="archiveModalOkVariant"
      cancel-title="Annuler"
      @ok="doArchive"
    >
      <p>Voulez-vous vraiment {{ archiveConfirmationActionLabel }} la demande de <strong>{{ selectedRequest && selectedRequest.firstName }} {{ selectedRequest && selectedRequest.lastName }}</strong> ?</p>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BTable,
  BButton,
  BFormCheckbox,
  BFormInput,
  BFormSelect,
  BPagination,
  BSpinner,
  BCollapse,
  BModal,
  BAlert,
  BAvatar,
  BBadge,
  BMedia,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  VBTooltip,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import flatPickr from 'vue-flatpickr-component'
import { French as FrenchLocale } from 'flatpickr/dist/l10n/fr'
import vSelect from 'vue-select'
import accommodationStoreModule, { DEFAULT_PAGE_SIZE } from './accommodationStoreModule'

const ACCOMMODATION_STORE_MODULE_NAME = 'accommodation'
const EXPORT_BASE_FILENAME = 'Demandes-hebergement'
const INTERACTIVE_TARGET_SELECTORS = [
  'button',
  '.no-row-click',
  '.flatpickr-input',
  '.input-group-merge',
  '.vs__dropdown-toggle',
  '.history-toggle-btn',
]

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BButton,
    BFormCheckbox,
    BFormInput,
    BFormSelect,
    BPagination,
    BSpinner,
    BCollapse,
    BModal,
    BAlert,
    BAvatar,
    BBadge,
    BMedia,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    flatPickr,
    vSelect,
  },
  data() {
    return {
      showArchived: false,
      showExportBar: false,
      selectedRequest: null,
      searchTerm: '',
      currentPage: 1,
      perPage: DEFAULT_PAGE_SIZE,
      perPageOptions: [10, 20, 50],
      exportStart: null,
      exportEnd: null,
      isExporting: false,
      exportError: null,
      exportRangeConfig: {
        wrap: true,
        mode: 'range',
        dateFormat: 'Z',
        altInput: true,
        altFormat: 'd/m/Y',
        locale: FrenchLocale,
        allowInput: false,
        onChange: selectedDates => {
          this.exportStart = selectedDates[0] ? selectedDates[0].toISOString() : null
          this.exportEnd = selectedDates[1] ? selectedDates[1].toISOString() : null
          this.exportError = null
        },
      },
      flatPickrConfig: {
        wrap: true,
        enableTime: true,
        dateFormat: 'Z',
        altInput: true,
        altFormat: 'd/m/Y H:i',
        time_24hr: true,
        locale: FrenchLocale,
        allowInput: false, // Empêche la saisie manuelle pour éviter les formats invalides
      },
      fields: [
        {
          key: 'receivedAt',
          label: 'Reçu le',
          sortable: true,
          tdClass: 'align-middle text-nowrap',
          thStyle: { width: '140px' },
        },
        {
          key: 'youngDetails',
          label: 'Jeune',
          sortable: true,
          sortBy: 'lastName',
          tdClass: 'align-middle',
          thStyle: { minWidth: '270px' },
        },
        {
          key: 'department',
          label: 'Dép.',
          sortable: true,
          tdClass: 'align-middle text-nowrap',
          thStyle: { width: '80px' },
        },
        {
          key: 'status',
          label: 'Statut',
          tdClass: 'align-middle text-nowrap',
          thStyle: { width: '130px' },
        },
        {
          key: 'appointmentDateTime',
          label: 'Date RDV',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'no-row-click align-middle',
          thStyle: { minWidth: '190px', width: '190px' },
        },
        {
          key: 'followUpStatus',
          label: 'Suivi de la demande',
          thClass: 'text-center',
          tdClass: 'no-row-click align-middle',
          thStyle: { minWidth: '340px', width: '340px' },
        },
        {
          key: 'actions',
          label: 'Actions',
          thClass: 'text-center',
          tdClass: 'no-row-click align-middle',
          thStyle: { width: '110px' },
        },
      ],
      historyFields: [
        { key: 'receivedAt', label: 'Reçu le', tdClass: 'align-middle text-nowrap' },
        { key: 'status', label: 'Statut', tdClass: 'align-middle' },
        { key: 'followUpStatus', label: 'Suivi', tdClass: 'align-middle' },
        { key: 'appointmentDateTime', label: 'RDV', tdClass: 'align-middle' },
        {
          key: 'historyActions', label: '', thClass: 'text-right', tdClass: 'align-middle text-right',
        },
      ],
      expandedYoungIds: new Set(),
    }
  },
  computed: {
    ...mapGetters({
      requests: 'accommodation/requests',
      loading: 'accommodation/loading',
      error: 'accommodation/error',
      pagination: 'accommodation/pagination',
      permissions: 'initialData/getPermissions',
      taxonomies: 'initialData/getTaxonomies',
    }),
    isEcoutant() {
      return store.getters['users/getUserRole']?.includes('Ecoutant')
    },
    canModifyAccommodationRequests() {
      return [
        'AccommodationRequest.Create',
        'AccommodationRequest.Update',
        'AccommodationRequest.Archive',
      ].some(permission => this.permissions.includes(permission))
    },
    isAccommodationReadOnly() {
      return this.isEcoutant || (this.permissions.length > 0 && !this.canModifyAccommodationRequests)
    },
    isSelectedRequestArchived() {
      return !!(this.selectedRequest && this.selectedRequest.isArchived)
    },
    archiveModalTitle() {
      return this.isSelectedRequestArchived ? 'Confirmer l\'activation' : 'Confirmer l\'archivage'
    },
    archiveModalOkTitle() {
      return this.isSelectedRequestArchived ? 'Activer' : 'Archiver'
    },
    archiveModalOkVariant() {
      return this.isSelectedRequestArchived ? 'success' : 'danger'
    },
    archiveConfirmationActionLabel() {
      return this.isSelectedRequestArchived ? 'activer' : 'archiver'
    },
    exportRangeValue() {
      const selectedDates = [this.exportStart, this.exportEnd].filter(Boolean)
      return selectedDates.length ? selectedDates : []
    },
    followUpStatusOptions() {
      return this.taxonomies.accommodationFollowUpStatus || []
    },
    groupedRequests() {
      const groupsByYoung = new Map()

      this.requests.forEach(request => {
        const key = request.youngId
        if (!groupsByYoung.has(key)) {
          groupsByYoung.set(key, [])
        }
        groupsByYoung.get(key).push(request)
      })

      return Array.from(groupsByYoung.values()).map(items => {
        const history = [...items].sort((a, b) => new Date(b.receivedAt) - new Date(a.receivedAt))
        const activeRequest = history.find(item => !item.isArchived)
        const representative = activeRequest || history[0]
        const hasArchivedRequest = history.some(item => item.isArchived)
        const isFollowUp = history.length > 1 && !!activeRequest && hasArchivedRequest

        return {
          ...representative,
          _requestCount: history.length,
          _isFollowUp: isFollowUp,
          _history: history,
          _showDetails: this.expandedYoungIds.has(representative.youngId),
        }
      })
    },
    paginationInfo() {
      const { currentPage, pageSize, totalCount } = this.pagination
      const from = ((currentPage - 1) * pageSize) + 1
      const to = Math.min(currentPage * pageSize, totalCount)
      return `${from}-${to} sur ${totalCount} demandes`
    },
    isExportRangeValid() {
      if (!this.exportStart && !this.exportEnd) {
        return true // Autoriser l'export total si aucune date n'est sélectionnée
      }
      return !!this.exportStart && !!this.exportEnd
    },
  },
  watch: {
    perPage() {
      this.loadRequests(1)
    },
  },
  created() {
    if (!store.hasModule(ACCOMMODATION_STORE_MODULE_NAME)) {
      store.registerModule(ACCOMMODATION_STORE_MODULE_NAME, accommodationStoreModule)
    }

    if (!this.followUpStatusOptions.length || !this.permissions.length) {
      this.loadFollowUpStatusOptions()
    }

    this.loadRequests()
  },
  beforeDestroy() {
    if (store.hasModule(ACCOMMODATION_STORE_MODULE_NAME)) {
      store.unregisterModule(ACCOMMODATION_STORE_MODULE_NAME)
    }
  },
  methods: {
    ...mapActions({
      fetchRequests: 'accommodation/fetchRequests',
      updateAppointmentAction: 'accommodation/updateAppointment',
      archiveRequest: 'accommodation/archiveRequest',
      updateFollowUpStatusAction: 'accommodation/updateFollowUpStatus',
      exportRequests: 'accommodation/exportRequests',
    }),
    loadFollowUpStatusOptions() {
      store.dispatch('initialData/fetchInitialData').catch(error => {
        console.error('Failed to fetch initial data:', error)
        this.showError('Erreur lors du chargement des statuts de suivi')
      })
    },
    loadRequests(pageNumber = 1) {
      this.currentPage = pageNumber
      this.fetchRequests({
        pageNumber,
        pageSize: this.perPage,
        includeArchived: this.showArchived,
        searchTerm: this.searchTerm,
      }).catch(error => {
        console.error('Failed to fetch accommodation requests:', error)
        this.showError('Erreur lors du chargement des demandes')
      })
    },
    onSearchChange() {
      this.loadRequests(1)
    },
    onPageChange(page) {
      this.loadRequests(page)
    },
    onIncludeArchivedChange() {
      this.loadRequests(1)
    },
    resetAllFilters() {
      this.searchTerm = ''
      this.showArchived = false
      this.loadRequests(1)
    },
    clearSearch() {
      this.searchTerm = ''
      this.loadRequests(1)
    },
    clearError() {
      store.commit('accommodation/setError', null)
    },
    formatDate(dateString) {
      if (!dateString) {
        return '-'
      }
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    onRowClicked(item, index, event) {
      // Ignorer si le clic provient d'un élément interactif qui gère déjà son propre clic
      const { target } = event

      if (this.isInteractiveTarget(target)) {
        return
      }

      this.openYoungDetails(item.youngId)
    },
    isInteractiveTarget(target) {
      if (!target) {
        return false
      }

      if (target.tagName === 'INPUT' || target.tagName === 'BUTTON') {
        return true
      }

      if (typeof target.closest !== 'function') {
        return false
      }

      return INTERACTIVE_TARGET_SELECTORS.some(selector => target.closest(selector))
    },
    getRowClass(item, type) {
      if (!item || type !== 'row') {
        return 'accommodation-row'
      }

      return item.isArchived
        ? 'accommodation-row accommodation-row-archived'
        : 'accommodation-row'
    },
    getScrollableAncestors(element) {
      const scrollableAncestors = [window]
      let currentElement = element?.parentElement

      while (currentElement && currentElement !== document.body) {
        const { overflow, overflowX, overflowY } = window.getComputedStyle(currentElement)
        const isScrollable = [overflow, overflowX, overflowY].some(value => /(auto|scroll|overlay)/.test(value))

        if (isScrollable) {
          scrollableAncestors.push(currentElement)
        }

        currentElement = currentElement.parentElement
      }

      return scrollableAncestors
    },
    calculateFollowUpDropdownPosition(dropdownList, component) {
      const toggleElement = component?.$refs?.toggle

      if (!toggleElement) {
        return undefined
      }

      const dropdownStyle = dropdownList.style

      const updatePosition = () => {
        const { bottom, left, width } = toggleElement.getBoundingClientRect()
        const scrollX = window.scrollX || window.pageXOffset
        const scrollY = window.scrollY || window.pageYOffset

        dropdownStyle.top = `${Math.round(bottom + scrollY + 4)}px`
        dropdownStyle.left = `${Math.round(left + scrollX)}px`
        dropdownStyle.width = `${Math.round(width)}px`
        dropdownStyle.zIndex = '1080'
      }

      const schedulePositionUpdate = () => {
        window.requestAnimationFrame(updatePosition)
      }

      const scrollableAncestors = this.getScrollableAncestors(toggleElement)

      updatePosition()

      scrollableAncestors.forEach(scrollableAncestor => {
        scrollableAncestor.addEventListener('scroll', schedulePositionUpdate, { passive: true })
      })
      window.addEventListener('resize', schedulePositionUpdate, { passive: true })

      return () => {
        scrollableAncestors.forEach(scrollableAncestor => {
          scrollableAncestor.removeEventListener('scroll', schedulePositionUpdate)
        })
        window.removeEventListener('resize', schedulePositionUpdate)
      }
    },
    handleUpdateAppointment(item, newDate) {
      if (this.isAccommodationReadOnly) {
        return
      }

      const currentTimestamp = item.appointmentDateTime ? new Date(item.appointmentDateTime).getTime() : 0
      const newTimestamp = newDate ? new Date(newDate).getTime() : 0

      if (newTimestamp !== currentTimestamp) {
        this.updateAppointmentAction({
          id: item.id,
          appointmentDateTime: new Date(newDate).toISOString(),
        })
          .then(() => {
            this.showSuccess('Date de rendez-vous mise à jour')
          })
          .catch(error => {
            console.error('Failed to update appointment:', error)
            this.showError('Erreur lors de la mise à jour')
          })
      }
    },
    openYoungDetails(youngId) {
      this.$router.push({ name: 'youngs', query: { youngId } })
    },
    getFollowUpStatusLabel(followUpStatus) {
      return this.followUpStatusOptions.find(option => String(option.id) === String(followUpStatus))?.label || '—'
    },
    handleFollowUpStatusChange(item, newStatus) {
      if (this.isAccommodationReadOnly) {
        return
      }

      this.updateFollowUpStatusAction({ id: item.id, followUpStatus: newStatus })
        .then(() => {
          this.showSuccess('Suivi mis à jour')
        })
        .catch(error => {
          console.error('Failed to update follow-up status:', error)
          this.showError('Erreur lors de la mise à jour du suivi')
        })
    },
    clearExportRange() {
      if (this.$refs.exportPicker && this.$refs.exportPicker.fp) {
        this.$refs.exportPicker.fp.clear()
      }
      this.exportStart = null
      this.exportEnd = null
      this.exportError = null
    },
    formatExportFilenameDate(dateString) {
      return new Date(dateString).toLocaleDateString('fr-FR').replaceAll('/', '-')
    },
    async handleExport() {
      if (this.isAccommodationReadOnly || !this.isExportRangeValid) {
        return
      }

      this.isExporting = true
      this.exportError = null
      try {
        const blob = await this.exportRequests({ start: this.exportStart, end: this.exportEnd, includeArchived: this.showArchived })

        const archiveSuffix = this.showArchived ? '_avec-archives' : '_sans-archives'
        let filename = `${EXPORT_BASE_FILENAME}${archiveSuffix}_Toutes.xlsx`
        if (this.exportStart && this.exportEnd) {
          const startStr = this.formatExportFilenameDate(this.exportStart)
          const endStr = this.formatExportFilenameDate(this.exportEnd)
          filename = `${EXPORT_BASE_FILENAME}${archiveSuffix}_${startStr}_${endStr}.xlsx`
        }

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        this.clearExportRange()
        this.showExportBar = false
        this.showSuccess('Export téléchargé avec succès')
      } catch {
        this.exportError = 'Une erreur est survenue lors de l\'export. Veuillez réessayer.'
      } finally {
        this.isExporting = false
      }
    },
    confirmArchive(request) {
      if (this.isAccommodationReadOnly) {
        return
      }

      this.selectedRequest = request
      this.$refs.archiveModal.show()
    },
    doArchive() {
      if (this.isAccommodationReadOnly || !this.selectedRequest) {
        return
      }

      if (this.selectedRequest) {
        const isReactivating = this.selectedRequest.isArchived

        this.archiveRequest(this.selectedRequest.id)
          .then(() => {
            this.showSuccess(isReactivating ? 'Demande réactivée avec succès' : 'Demande archivée avec succès')
          })
          .catch(error => {
            console.error('Failed to archive request:', error)
            this.showError(isReactivating ? 'Erreur lors de la réactivation' : "Erreur lors de l'archivage")
          })
        this.selectedRequest = null
      }
    },
    showToast({
      title,
      icon,
      variant,
      text,
    }) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
          text,
        },
      })
    },
    showSuccess(message) {
      this.showToast({
        title: 'Succès',
        icon: 'CheckIcon',
        variant: 'success',
        text: message,
      })
    },
    showError(message) {
      this.showToast({
        title: 'Erreur',
        icon: 'AlertTriangleIcon',
        variant: 'danger',
        text: message,
      })
    },
    getInitials(firstName, lastName) {
      const f = firstName ? firstName.charAt(0) : ''
      const l = lastName ? lastName.charAt(0) : ''
      return (f + l).toUpperCase()
    },
    toggleGroupDetails(item) {
      if (this.expandedYoungIds.has(item.youngId)) {
        this.expandedYoungIds.delete(item.youngId)
      } else {
        this.expandedYoungIds.add(item.youngId)
      }
      this.expandedYoungIds = new Set(this.expandedYoungIds)
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style lang="scss" scoped>
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/components/variables-dark';

.accommodation-requests {
  padding: 1rem;

  .export-bar-content,
  .export-bar-controls {
    gap: 1rem;
  }

  .export-range-input {
    min-width: 250px;
    max-width: 400px;
  }

  .export-hint {
    min-height: 24px;
  }

  .archive-state-badge {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
  }

  .follow-up-request-badge {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
  }

  .history-count-badge {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
  }

  .history-toggle-btn {
    flex-shrink: 0;
  }

  .history-panel {
    background-color: rgba(115, 103, 240, 0.03);
    border-radius: 0.357rem;
  }

  .dark-layout & .history-panel {
    background-color: rgba(115, 103, 240, 0.08);
  }

  .young-details-header {
    gap: 0.5rem;
  }

  .follow-up-cell {
    min-width: 340px;
    position: relative;
    overflow: visible;
  }

  .follow-up-value {
    display: block;
    min-width: 340px;
  }

  .appointment-cell {
    min-width: 190px;
  }

  .actions-cell {
    min-width: 110px;
  }

  .export-bar {
    background: rgba(115, 103, 240, 0.04);
    transition: all 0.3s ease;
  }

  ::v-deep .accommodation-row td {
    cursor: pointer;
    vertical-align: middle;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  ::v-deep .accommodation-row.accommodation-row-archived td {
    background-image: linear-gradient(rgba(115, 103, 240, 0.25), rgba(115, 103, 240, 0.25));
  }

  ::v-deep .accommodation-row.accommodation-row-archived td:first-child {
    box-shadow: inset 4px 0 0 #7367f0;
  }

  ::v-deep .accommodation-row.accommodation-row-archived:hover td {
    background-image: linear-gradient(rgba(115, 103, 240, 0.25), rgba(115, 103, 240, 0.25));
  }

  ::v-deep .accommodation-row td.no-row-click {
    cursor: default;
  }

  ::v-deep .appointment-cell .flatpickr-input ~ .form-control[readonly] {
    background-color: $white;
  }

  ::v-deep .follow-up-select .vs__dropdown-toggle {
    background-color: $white;
  }

  .dark-layout & {
    ::v-deep .appointment-cell .flatpickr-input ~ .form-control[readonly] {
      background-color: $theme-dark-card-bg;
    }

    ::v-deep .follow-up-select .vs__dropdown-toggle {
      background-color: $theme-dark-input-bg;
    }
  }

  ::v-deep .follow-up-cell .v-select {
    width: 100%;
  }

  ::v-deep .follow-up-cell .v-select.vs--open {
    z-index: 20;
  }

  ::v-deep .follow-up-select .vs__dropdown-toggle {
    min-height: calc(1.5em + 0.857rem + 2px);
    width: 100%;
  }

  ::v-deep .follow-up-select .vs__selected-options {
    flex-wrap: nowrap;
    overflow: hidden;
  }

  ::v-deep .follow-up-select .vs__selected,
  ::v-deep .follow-up-select .vs__search,
  ::v-deep .follow-up-select .vs__search::placeholder {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ::v-deep .follow-up-select .vs__actions {
    padding-right: 0.5rem;
  }

  .hover-danger:hover {
    color: #ea5455 !important;
  }

  .search-input-group {
    background-color: $white;
    transition: background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
    border: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden; // Ensures children are clipped and appear as one block

    .input-group-text,
    .form-control {
      background-color: transparent !important;
      border: none !important;
      color: $body-color;
      transition: color 0.3s ease;
    }

    &:focus-within {
      border-color: #7367f0 !important;
      box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.15) !important;
    }

    .dark-layout & {
      background-color: $theme-dark-input-bg;
      border-color: $theme-dark-border-color;

      .input-group-text,
      .form-control {
        color: $theme-dark-body-color;
      }
    }
  }

  .cursor-help {
    cursor: help;
  }

  .animate-pulse {
    animation: pulse-opacity 1.5s infinite;
  }

  @keyframes pulse-opacity {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  .pagination-panel {
    width: auto;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);

    &__select {
      width: auto;
    }
  }

  .displaynbResult {
    display: flex;
    width: 100%;
    justify-content: flex-end;
    margin-bottom: 0;
    font-size: 0.857rem;
    color: $text-muted;
  }

  .dark-layout & {
    .pagination-panel {
      border-top-color: $theme-dark-border-color;
    }
  }
}
</style>
